import { RefreshCwIcon, ServerCrashIcon, ShieldOffIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { ApiError, isDatabaseWaking } from "@/api/client";
import { useMe, type Me } from "@/api/me";
import { useSignOut } from "@/auth/AuthGate";
import { FullPage, PulseLogo, Splash } from "@/components/full-page";
import { Button } from "@/components/ui/button";
import { DatabaseWaking } from "./DatabaseWaking";

/**
 * Loads /api/me and renders children with the user, or a loading / error screen. /me is the first request to touch
 * the database, so this is where a sign-in after an idle spell waits for the paused database to resume.
 */
export function MeBoundary({ children }: { children: (me: Me) => ReactNode }) {
  const { data, isPending, error, failureReason, refetch, isRefetching } = useMe();
  const signOut = useSignOut();
  const [since] = useState(() => Date.now());

  if (isPending) {
    // The query client keeps retrying while the API reports the database is waking.
    if (isDatabaseWaking(failureReason)) return <DatabaseWaking since={since} />;
    return <Splash label="Loading your workspace…" />;
  }

  if (error || !data) {
    const status = error instanceof ApiError ? error.status : undefined;
    const forbidden = status === 403;
    const waking = isDatabaseWaking(error);
    const Icon = forbidden ? ShieldOffIcon : ServerCrashIcon;
    return (
      <FullPage>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <PulseLogo className="mx-auto mb-5" />
          <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-xl border bg-muted">
            <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-base font-semibold">
            {forbidden
              ? "You don't have access to Pulse"
              : waking
                ? "The database is taking longer than usual to start"
                : "We couldn't reach the Pulse API"}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {forbidden
              ? "Your account signed in, but it isn't in any Pulse access group. Ask an administrator to add you."
              : waking
                ? "It's still waking up after a quiet spell. Try again in a minute; if it keeps happening, tell an administrator."
                : (error?.message ?? "Unknown error.")}
          </p>
          {!forbidden && !waking && import.meta.env.DEV && (
            <p className="mt-3 text-[13px] text-muted-foreground">
              Is the backend running on <code className="font-mono text-[12px]">http://127.0.0.1:8000</code>?
            </p>
          )}
          <div className="mt-6 flex justify-center gap-2">
            {!forbidden && (
              <Button onClick={() => void refetch()} disabled={isRefetching}>
                <RefreshCwIcon className={isRefetching ? "animate-spin" : undefined} /> Try again
              </Button>
            )}
            <Button variant="outline" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </div>
      </FullPage>
    );
  }

  return <>{children(data)}</>;
}
